import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation, usPhoneNumberValidation } from 'validators';
import { Page, Button, TextField, PasswordField, Card, USPhoneNumberInput, TextButton, Typography } from 'seagull';
import ChangeEmailAddressModal from '~/components/ChangeEmailAddressModal/ChangeEmailAddressModal';
import { useIoContext, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { errorTextsPath } from '~/infra/ErrorResponse';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { passwordPolicyTextsPath, validatePassword } from '~/utils/password-policy';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { DeleteAccountConfirmationModal } from './DeleteAccountConfirmationModal/DeleteAccountConfirmationModal';
import styles from './Profile.module.scss';
import { buildAccountDeletionRequestText } from './Profile.utils';
const testPrefix = 'profile';
const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 30;
const NAME_FORBIDDEN_CHARS_REGEX = /[=<>]/;
const nullIfEmpty = (s)=>s || null;
const buildNameValidator = (texts)=>({
        validate: {
            name: (value)=>{
                if (value == null || typeof value === 'string' && !value.trim()) {
                    return texts.required;
                }
                if (typeof value !== 'string') {
                    return true;
                }
                if (value.length > NAME_MAX_LENGTH) {
                    return texts.tooLong;
                }
                if (value.trim().length < NAME_MIN_LENGTH) {
                    return texts.tooShort;
                }
                if (NAME_FORBIDDEN_CHARS_REGEX.test(value)) {
                    return texts.invalidChars;
                }
                return true;
            }
        },
        dataAttr: {
            'data-validation': 'name'
        }
    });
export default function Profile() {
    const overlayContext = useOverlayContext();
    const { native } = useIoContext();
    const userInfo = useStore((state)=>state.userInfo);
    const isViewMode = useStore(selectors.isViewMode);
    const impersonatingAs = useStore(selectors.impersonatingAs);
    const { setUserInfo, logBI, api } = useStoreActions();
    const { control, formState, handleSubmit, watch, trigger, clearErrors, reset } = useForm({
        mode: 'onChange'
    });
    const userLoggedInViaLink = userInfo === null || userInfo === void 0 ? void 0 : userInfo.loggedInViaLink();
    const userHasPassword = userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasPassword();
    const newPasswordValue = watch('newPassword', '');
    const newPasswordUserInputs = [
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
        userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()
    ].filter(Boolean);
    const newPasswordStrengthScore = usePasswordStrength(newPasswordValue !== null && newPasswordValue !== void 0 ? newPasswordValue : '', newPasswordUserInputs);
    const { t: profileTexts } = useLocaleKeys('pages.profile');
    const { t: errorTexts } = useLocaleKeys(errorTextsPath);
    const { t: pwValidationTexts } = useLocaleKeys(passwordPolicyTextsPath);
    const firstNameValidator = buildNameValidator({
        required: profileTexts.validation.firstNameRequired(),
        invalidChars: profileTexts.validation.firstNameInvalidChars(),
        tooShort: profileTexts.validation.firstNameTooShort({
            count: NAME_MIN_LENGTH
        }),
        tooLong: profileTexts.validation.firstNameTooLong({
            count: NAME_MAX_LENGTH
        })
    });
    const lastNameValidator = buildNameValidator({
        required: profileTexts.validation.lastNameRequired(),
        invalidChars: profileTexts.validation.lastNameInvalidChars(),
        tooShort: profileTexts.validation.lastNameTooShort({
            count: NAME_MIN_LENGTH
        }),
        tooLong: profileTexts.validation.lastNameTooLong({
            count: NAME_MAX_LENGTH
        })
    });
    const currentPasswordValidator = requiredValidation(!!watch('newPassword') && profileTexts.validation.currentPasswordRequired());
    const newPasswordValidator = {
        validate (password) {
            const hasPassword = !!password;
            hasPassword ? trigger('currentPassword') : clearErrors('currentPassword');
            if (!hasPassword) return true;
            return validatePassword(password, [
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
                userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName()
            ].filter(Boolean), pwValidationTexts);
        }
    };
    const confirmPasswordValidator = {
        validate (password) {
            return password === watch('newPassword') || profileTexts.validation.passwordsMustMatch();
        }
    };
    useSetDocumentTitle(profileTexts.title());
    const onEditUserEmail = ()=>{
        overlayContext.showModal({
            biName: 'change_email_address_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ChangeEmailAddressModal, {
                    onClose: dismissModal
                });
            }
        });
    };
    const onDeleteAccount = async ()=>{
        const confirmation = await overlayContext.showModal({
            biName: 'delete_account_modal',
            content: (p)=>{
                var _ref;
                return /*#__PURE__*/ _jsx(DeleteAccountConfirmationModal, _object_spread({
                    expectedEmail: (_ref = userInfo === null || userInfo === void 0 ? void 0 : userInfo.email()) !== null && _ref !== void 0 ? _ref : ''
                }, p));
            }
        });
        if (!(confirmation === null || confirmation === void 0 ? void 0 : confirmation.confirmed)) {
            return;
        }
        const result = await handleRequest(api.slack.sendCsNotification(buildAccountDeletionRequestText(userInfo !== null && userInfo !== void 0 ? userInfo : null)));
        if (result.success) {
            overlayContext.showSuccessToast(profileTexts.deleteAccount.requestReceived(), "".concat(testPrefix, "-delete-account-success"));
        } else {
            overlayContext.showErrorToast(profileTexts.deleteAccount.requestFailed(), "".concat(testPrefix, "-delete-account-error"));
        }
    };
    const onSubmit = async (form)=>{
        logBI(10410);
        const updatedUserInfo = await handleRequest(api.auth.editUserDetails({
            name: {
                first: form.firstName,
                last: form.lastName
            },
            password: nullIfEmpty(form.newPassword),
            currentPassword: form.newPassword ? nullIfEmpty(form.currentPassword) : null,
            phone: nullIfEmpty(form.phone),
            clearPhoneNumber: !form.phone
        }));
        if (updatedUserInfo.success) {
            setUserInfo((userInfo === null || userInfo === void 0 ? void 0 : userInfo.updateUserDetails(updatedUserInfo.data)) || null);
            reset({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            overlayContext.showSuccessToast(profileTexts.changesSaved());
        } else {
            overlayContext.showErrorToast(updatedUserInfo.error.updateUserDetailsErrorText(errorTexts));
        }
    };
    return /*#__PURE__*/ _jsx(Page, {
        header: profileTexts.title(),
        skin: "secondary",
        testId: "".concat(testPrefix, "-page"),
        mobile: {
            showActions: true
        },
        actions: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'save_profile_changes'
            },
            intent: Intent.UpdateProfile,
            type: "submit",
            form: testPrefix,
            loading: formState.isSubmitting,
            testId: "".concat(testPrefix, "-btn-save-changes"),
            children: profileTexts.saveChanges()
        }),
        children: /*#__PURE__*/ _jsx("form", {
            id: testPrefix,
            onSubmit: handleSubmit(onSubmit),
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.root,
                children: [
                    /*#__PURE__*/ _jsxs(Section, {
                        title: profileTexts.personalDetails.title(),
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "firstName",
                                defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName(),
                                rules: firstNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_first_name'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field,
                                        mobile: {
                                            renderAsCard: true
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.personalDetails.firstName(),
                                        testId: "".concat(testPrefix, "-first-name"),
                                        disabled: isViewMode
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "lastName",
                                defaultValue: userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName(),
                                rules: lastNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_last_name'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field,
                                        mobile: {
                                            renderAsCard: true
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.personalDetails.lastName(),
                                        testId: "".concat(testPrefix, "-last-name"),
                                        disabled: isViewMode
                                    }))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: styles.emailContainer,
                                children: [
                                    /*#__PURE__*/ _jsx(TextField, {
                                        bi: {
                                            name: 'profile_email'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field,
                                        mobile: {
                                            renderAsCard: true
                                        },
                                        value: userInfo === null || userInfo === void 0 ? void 0 : userInfo.email(),
                                        label: profileTexts.personalDetails.emailAddress(),
                                        type: "email",
                                        testId: "".concat(testPrefix, "-email"),
                                        disabled: true
                                    }),
                                    /*#__PURE__*/ _jsx(TextButton, {
                                        bi: {
                                            name: 'edit_personal_details'
                                        },
                                        onClick: onEditUserEmail,
                                        underline: "none",
                                        testId: "".concat(testPrefix, "-email-edit-button"),
                                        type: "button",
                                        disabled: isViewMode,
                                        children: profileTexts.personalDetails.edit()
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "phone",
                                defaultValue: (userInfo === null || userInfo === void 0 ? void 0 : userInfo.phone()) || undefined,
                                rules: usPhoneNumberValidation(),
                                render: (renderProps)=>/*#__PURE__*/ _jsx(USPhoneNumberInput, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_phone'
                                        },
                                        collapseHelperText: false,
                                        className: styles.field,
                                        mobile: {
                                            renderAsCard: true
                                        }
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.personalDetails.phoneNumber(),
                                        testId: "".concat(testPrefix, "-phone"),
                                        disabled: isViewMode
                                    }))
                            })
                        ]
                    }),
                    !isViewMode && (userHasPassword || userLoggedInViaLink) && /*#__PURE__*/ _jsxs(Section, {
                        title: profileTexts.password.title(),
                        children: [
                            !userLoggedInViaLink && /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "currentPassword",
                                rules: currentPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_current_password'
                                        },
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.password.currentPassword(),
                                        testId: "".concat(testPrefix, "-current-password")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "newPassword",
                                rules: newPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_new_password'
                                        },
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        showStrengthMeter: true,
                                        strengthScore: newPasswordStrengthScore,
                                        label: profileTexts.password.newPassword(),
                                        testId: "".concat(testPrefix, "-new-password")
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "confirmPassword",
                                rules: confirmPasswordValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                        bi: {
                                            name: 'profile_confirm_password'
                                        },
                                        className: styles.field
                                    }, connectControllerToTextField(renderProps)), {
                                        label: profileTexts.password.confirmPassword(),
                                        testId: "".concat(testPrefix, "-confirm-password")
                                    }))
                            })
                        ]
                    }),
                    native.isNative && /*#__PURE__*/ _jsxs(Section, {
                        title: profileTexts.deleteAccount.title(),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: profileTexts.deleteAccount.description()
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                bi: {
                                    name: 'delete_account'
                                },
                                skin: "danger",
                                type: "button",
                                onClick: onDeleteAccount,
                                testId: "".concat(testPrefix, "-btn-delete-account"),
                                disabled: isViewMode || impersonatingAs != null,
                                children: profileTexts.deleteAccount.button()
                            })
                        ]
                    })
                ]
            })
        })
    });
}
const Section = (props)=>{
    return /*#__PURE__*/ _jsx(Card, {
        bi: {
            name: 'profile_section_card',
            data: {
                section: props.title
            }
        },
        padding: "m",
        autoHeight: true,
        title: props.title,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.content,
            children: props.children
        })
    });
};
